import {Component, EffectRef, EventEmitter, Input, OnDestroy, OnInit, Output, Signal, WritableSignal, computed, effect, inject, linkedSignal, signal} from '@angular/core';
import {DialogService} from "../../../shared/services/dialog.service";
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {StepHeaderComponent} from "../../../shared/elements/step-header/step-header.component";
import {LanguageService} from "../../../shared/services/language.service";
import {StepContentComponent} from "../../../shared/elements/step-content/step-content.component";
import {NumericTextBoxModule, TextAreaModule, TextBoxModule} from "@syncfusion/ej2-angular-inputs";
import {DropDownListModule} from "@syncfusion/ej2-angular-dropdowns";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators, FormsModule} from "@angular/forms";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {FormTextareaComponent} from "../../../shared/elements/form-textarea/form-textarea.component";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {arrayValidator} from "../../../shared/validators/array.directive";
import {FormDropdownComponent} from "../../../shared/elements/form-dropdown/form-dropdown.component";
import {FormMultiSelectComponent} from "../../../shared/elements/form-multi-select/form-multi-select.component";
import {DatePickerModule, DateTimePickerModule, TimePickerModule} from "@syncfusion/ej2-angular-calendars";
import {RadioButtonModule, CheckBoxModule} from "@syncfusion/ej2-angular-buttons";
import {FormRadioComponent} from "../../../shared/elements/form-radio/form-radio.component";
import {FormNumericComponent} from "../../../shared/elements/form-numeric/form-numeric.component";
import {FormDateTimeComponent} from "../../../shared/elements/form-date-time/form-date-time.component";
import {FormUserGroupMailComponent} from "../../../shared/elements/form-user-group-mail/form-user-group-mail.component";
import {FormCheckboxComponent} from "../../../shared/elements/form-checkbox/form-checkbox.component";
import {FormDateComponent} from "../../../shared/elements/form-date/form-date.component";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {Subject, catchError, debounceTime, map, of, switchMap, takeUntil} from 'rxjs';
import {ToasterService} from "../../../shared/services/toaster.service";
import {buildRecurrenceString} from '../mapper/form-to-calendar.mapper';
import {calendarTimeStepMinutes, decodeRrule, endTimeCalculation, formatTimeAndMinuteToDate, formatTimeIgnoringOffset, getEndDateTimeOneHourLater} from '../mapper/calendar-helper.mapper';
import {ScheduleComponent} from '@syncfusion/ej2-angular-schedule';
import {CalendarStore} from '../stores/calendar-store';
import {CalendarEventUpdateRequest} from '../../../shared/types/generated/calendarEventUpdateRequest';
import {createCalendarEventUpdateRequest} from '../mapper/calendar-api.mapper';
import {FormTimeComponent} from '../../../shared/elements/form-time/form-time.component';
import {ProfileService} from '../../../shared/services/profile.service';
import {StepItem} from '../../../shared/types/help-types/stepsInterface';
import {DialogDocumentsUploadComponent} from "../../documents/dialog-documents-upload/dialog-documents-upload.component";
import {FileStorageResource} from '../../../shared/types/generated/fileStorageResource';
import {CalendarInventoryResource} from '../../../shared/types/generated/calendarInventoryResource';
import {CalendarEventInventoryResource} from '../../../shared/types/generated/calendarEventInventoryResource';
import {CalendarInventoryConflictMode} from '../../../shared/types/generated/calendarInventoryConflictMode';
import {CalendarInventoryCapacityMode} from '../../../shared/types/generated/calendarInventoryCapacityMode';
import {CalendarResource} from '../../../shared/types/generated/calendarResource';
import {CalendarEventResource} from '../../../shared/types/generated/calendarEventResource';
import {WarningComponent} from "../../../shared/elements/warning/warning.component";
import {ApiService} from "../../../shared/services/api.service";
import {CalendarEventRRuleValidationRequest} from "../../../shared/types/generated/calendarEventRRuleValidationRequest";
import {CalendarEventInventoryValidationRequest} from "../../../shared/types/generated/calendarEventInventoryValidationRequest";
import {CalendarEventInventoryValidationResource} from "../../../shared/types/generated/calendarEventInventoryValidationResource";
import {CalendarType} from '../../../shared/types/generated/calendarType';
import {ErrorComponent} from "../../../shared/elements/error/error.component";
import {CapabilitiesService} from "../../../shared/services/capabilities.service";
import {AsyncValidationStatus} from '../../../shared/types/calendar-types/asyncValidationStatus.type';
import {InventoryValidationDisplayState} from '../../../shared/types/calendar-types/inventoryValidationDisplayState.interface';
import {StepValidationState} from '../../../shared/types/calendar-types/stepValidationState.interface';

@Component({
    selector: 'app-dialog-calendar-event',
    imports: [
    DialogModule,
    StepHeaderComponent,
    StepContentComponent,
    TooltipModule,
    TextBoxModule,
    DropDownListModule,
    HeadlineComponent,
    TranslatePipe,
    FormButtonComponent,
    TooltipComponent,
    ReactiveFormsModule,
    FormsModule,
    FormLabelComponent,
    FormTextareaComponent,
    FormInputComponent,
    TextAreaModule,
    FormDropdownComponent,
    FormMultiSelectComponent,
    DatePickerModule,
    TimePickerModule,
    DateTimePickerModule,
    NumericTextBoxModule,
    RadioButtonModule,
    CheckBoxModule,
    FormRadioComponent,
    FormNumericComponent,
    FormDateTimeComponent,
    FormUserGroupMailComponent,
    FormCheckboxComponent,
    FormDateComponent,
    ContentComponent,
    FormTimeComponent,
    DialogDocumentsUploadComponent,
    WarningComponent,
    ErrorComponent
],
    templateUrl: './dialog-calendar-event.component.html',
    styleUrl: './dialog-calendar-event.component.scss'
})

export class DialogCalendarEventComponent implements OnInit, OnDestroy {
  private static readonly NAME_MAX_LENGTH: number = 255;
  private static readonly DESCRIPTION_MAX_LENGTH: number = 65535;
  private static readonly LOCATION_MAX_LENGTH: number = 250;

  public dialogService: DialogService = inject(DialogService);
  private languageService: LanguageService = inject(LanguageService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private toasterService: ToasterService = inject(ToasterService);
  private calendarStore: InstanceType<typeof CalendarStore> = inject(CalendarStore);
  private profileService: ProfileService = inject(ProfileService);
  private apiService: ApiService = inject(ApiService);
  private capabilitiesService: CapabilitiesService = inject(CapabilitiesService);

  @Input() public set calendarEvent(value: CalendarEventResource | null) {
    //if we are creating a new event in the mini calendar from the Dashboard we will pass value as null. 
    // If we are editing an event in the Dashboard or we are creating/editing an event from the Full Screen calendar we pass the value
    this._initialEvent = value;
  } 
  public get calendarEvent(): CalendarEventResource | null {
    return this._initialEvent;
  }
  @Input() public visible: boolean = false;
  @Input() public schedulerRef: ScheduleComponent | null = null;
  @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _initialEvent: CalendarEventResource | null = null;
  private readonly baseSteps: StepItem[] = [
    {id: 'details', title: this.languageService.getTranslationByKey('CALEVENTDETAILS'), help: this.languageService.getTranslationByKey('CALEVENTDETAILSHELP')},
    {id:'date', title: this.languageService.getTranslationByKey('CALEVENTDATELOC'), help: this.languageService.getTranslationByKey('CALEVENTDATELOCHELP')},
    {id:'guests', title: this.languageService.getTranslationByKey('CALEVENTGUESTS'),help: this.languageService.getTranslationByKey('CALEVENTGUESTSHELP'), optional:true}
  ];
  private readonly resourcesStep: StepItem = {
    id:'resources',
    title: this.languageService.getTranslationByKey('CALEVENTRESOURCES'),
    help: this.languageService.getTranslationByKey('CALEVENTRESOURCESHELP'),
    optional:true
  };
  private readonly settingsStep: StepItem = {
    id: 'settings',
    title: this.languageService.getTranslationByKey('CALEVENTSETTINGS'),
    help: this.languageService.getTranslationByKey('CALEVENTSETTINGSHELP'),
    optional: true
  };
  public activeStep: number = 0;
  public visibleQuickDialog: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public priorityOptions: any[] = [{id: 'high', name: this.languageService.getTranslationByKey('HIGH')}, {id: 'medium', name: this.languageService.getTranslationByKey('MEDIUM')}, {id: 'low', name: this.languageService.getTranslationByKey('LOW')}];
  public repeatTypeOptions: { id: string; name: string }[] = [
    {id: 'never', name: this.languageService.getTranslationByKey('NEVER')},
    {id: 'daily', name: this.languageService.getTranslationByKey('DAILY')},
    {id: 'weekly', name: this.languageService.getTranslationByKey('WEEKLY')},
    {id: 'monthly', name: this.languageService.getTranslationByKey('MONTHLY')},
    {id: 'yearly', name: this.languageService.getTranslationByKey('YEARLY')}
  ];
  public repeatEndOptions: { id: string; name: string }[] = [
    {id: 'never', name: this.languageService.getTranslationByKey('NEVER')},
    {id: 'until', name: this.languageService.getTranslationByKey('UNTIL')},
    {id: 'count', name: this.languageService.getTranslationByKey('COUNT')}
  ];
  public repeatUnit: string = 'DAYS';
  public showRepeatEvery: boolean = false;
  public weekdayOptions: { value: string; label: string }[] = [];

  public ordinalOptions: { id: string; name: string }[] = [
    {id: '1', name: this.languageService.getTranslationByKey('FIRST')},
    {id: '2', name: this.languageService.getTranslationByKey('SECOND')},
    {id: '3', name: this.languageService.getTranslationByKey('THIRD')},
    {id: '4', name: this.languageService.getTranslationByKey('FOURTH')},
    {id: '-1', name: this.languageService.getTranslationByKey('LAST')},
  ];

  public weekdayOptionsLong: { id: string; name: string }[] = [];
  public monthOptions: { id: number; name: string }[] = [];

  public events: Date[] = [];
  public eventsShowAll: boolean = false;
  public participantsUserIDs: number[] = [];
  public participantsGroupIDs: number[] = [];
  public participantsMails: string[] = [];
  public pushUserIDs: number[] = [];
  public pushGroupIDs: number[] = [];
  public participantsAllUsers: boolean = false;
  public participantsNotifyAllUsers: boolean = false;
  public attachmentFiles: FileStorageResource[] = [];
  public inventoryForm: FormGroup = this.formBuilder.group({});
  private selectedCalendarId: WritableSignal<(number | string) | null> = signal(null);
  public selectedCalendarConfig: Signal<CalendarResource['config'] | null> = linkedSignal(() => {
    const selectedCalendarId = this.selectedCalendarId();
    return selectedCalendarId === null ? null : (this.calendarStore.calendars().find((x) => x.id === selectedCalendarId)?.config ?? null);
  });
  public canInviteAllUsers: Signal<boolean> = computed(() =>
    this.capabilitiesService.capabilities().calendar?.calendars.invite_all_users ?? false
  );
  public availableInventories: Signal<CalendarInventoryResource[]> = linkedSignal(() => {
    const selectedCalendarId = this.selectedCalendarId();
    return selectedCalendarId === null ? [] : (this.calendarStore.calendarInventories().get(selectedCalendarId) ?? []);
  });
  public inventoryLoading: boolean = false;
  public selectedInventoryOptions: {id: number; name: string}[] = [];
  public calculatedEventCapacity: number | null = 0;
  public calendarStepMinutes: number = calendarTimeStepMinutes();
  public calendarEventForm!: FormGroup;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public customTimezone: any;
  private selectedZone: string;
  private suppressFormChangeReactions: boolean = false;
  private previousTimedRange: { start: Date; end: Date } | null = null;
  private eventToUpdate: CalendarEventUpdateRequest | null = null;
  private destroy$: Subject<void> = new Subject<void>();
  private recurrenceValidationTrigger$: Subject<void> = new Subject<void>();
  private inventoryValidationTrigger$: Subject<void> = new Subject<void>();
  private readonly validationDebounceMs: number = 250;
  private initialRecurrenceValidationKey: string | null = null;
  private initialInventoryValidationKey: string | null = null;
  
  // Track which sections have been edited
  public section0Edited: boolean = false; // Basic details (name, priority, description, location)
  public section1Edited: boolean = false; // Date/time and recurrence settings
  public section2Edited: boolean = false; // Participants
  public section3Edited: boolean = false; // Resources
  public adaptDetails: boolean = false;
  public adaptDateLocation: boolean = false;
  public adaptGuests: boolean = false;
  public adaptInventories: boolean = false;
  public recurrenceValidation: StepValidationState = this.createValidationState();
  public inventoryValidation: InventoryValidationDisplayState = this.createInventoryValidationState();

  public constructor() {
    this.capabilitiesService.ensureUserCapabilitiesLoaded();
    this.customTimezone = Intl.supportedValuesOf('timeZone').map((zone: string) => ({name: zone, id: zone}));
    this.selectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    effect(() => {
      this.calendarStore.calendarInventories();
      this.ensureActiveStepInRange();
    });

    effect(() => {
      if (this.canInviteAllUsers()) {
        return;
      }

      this.participantsAllUsers = false;
      this.participantsNotifyAllUsers = false;
    });
  }

  public ngOnInit(): void {
    this.buildForm();
    this.resetSectionEditTracking();
    this.subscribeToFormChanges();
    //if we are creating a new event in the mini calendar from the Dashboard we will pass value as null. 
    // If we are editing an event in the Dashboard or we are creating/editing an event from the Full Screen calendar we pass the value
    this.runWithoutFormChangeReactions(() => {
      if (this._initialEvent) {
        this.populateForm(this._initialEvent);
      } else {
        this.initializeFormDefaults();
      }
    });
    this.setupSectionEditTracking();
    this.setLocalizedOptions();
    this.setupAsyncValidation();
    this.captureInitialValidationBaseline();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get stepTitles(): string[] {
    return this.steps?.map(step => step.title) ?? [];
  }

  public get resourcesStepTitle(): string {
    return this.resourcesStep.title;
  }

  public get steps(): StepItem[] {
    return this.shouldShowResourcesStep
      ? [...this.baseSteps, this.resourcesStep, this.settingsStep]
      : [...this.baseSteps, this.settingsStep];
  }

  public get isOverrideEvent(): boolean {
    return this.calendarEvent?.override.parent_id !== null && this.calendarEvent?.override.parent_id !== undefined;
  }

  public availableCalendars: Signal<CalendarResource[]> = computed(() =>
    this.calendarStore.calendars().filter((cal) => cal.permission.fillable === true)
  );

  public get canEditCalendarSelection(): boolean {
    if (this.calendarEvent?.override.parent_id !== null && this.calendarEvent?.override.parent_id !== undefined) {
      return false;
    }

    return !this.calendarEvent?.id || this.calendarEvent.id === -1;
  }

  public get selectedCalendarName(): string {
    const selectedCalendarId = this.calendarEventForm?.get('calendar_id')?.value
      ?? this.calendarEvent?.calendar_id
      ?? this.calendarStore.selectedCalendarID();
    return this.getCalendarNameById(selectedCalendarId);
  }

  public get selectedCalendarInventories(): CalendarInventoryResource[] {
    const selectedCalendarId = this.calendarEventForm?.get('calendar_id')?.value
      ?? this.calendarEvent?.calendar_id
      ?? this.calendarStore.selectedCalendarID();

    if (selectedCalendarId === null || selectedCalendarId === undefined) {
      return [];
    }

    return this.calendarStore.calendarInventories().get(selectedCalendarId) ?? [];
  }

  public get isSelectedCalendarInventoryLoading(): boolean {
    const selectedCalendarId = this.calendarEventForm?.get('calendar_id')?.value
      ?? this.calendarEvent?.calendar_id
      ?? this.calendarStore.selectedCalendarID();

    if (selectedCalendarId === null || selectedCalendarId === undefined) {
      return false;
    }

    return !this.calendarStore.calendarInventories().has(selectedCalendarId);
  }

  public get shouldShowResourcesStep(): boolean {
    if (this.isInventoryValidationUnsupported()) {
      return false;
    }

    return this.isSelectedCalendarInventoryLoading || this.selectedCalendarInventories.length > 0;
  }

  //#region form handling
  private static readonly nonWhitespaceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (typeof value !== 'string') {
      return {required: true};
    }

    return value.trim() === '' ? {required: true} : null;
  };

  private static readonly validDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    return control.value instanceof Date && !Number.isNaN(control.value.getTime()) ? null : {invalidDate: true};
  };

  private buildForm() {
    this.calendarEventForm = this.formBuilder.group({
      calendar_id: [null, [Validators.required]],
      calendar_name_display: [''],
      name: ['', [Validators.required, DialogCalendarEventComponent.nonWhitespaceValidator, Validators.minLength(1), Validators.maxLength(DialogCalendarEventComponent.NAME_MAX_LENGTH)]],
      priority: ['medium', [Validators.required, arrayValidator(this.priorityOptions.map(item => item.id))]],
      description: ['', [Validators.maxLength(DialogCalendarEventComponent.DESCRIPTION_MAX_LENGTH)]],
      enableCustomLocation: [false, Validators.required],
      location: ['', [Validators.maxLength(DialogCalendarEventComponent.LOCATION_MAX_LENGTH)]],
      onDateStart: [null, [Validators.required, DialogCalendarEventComponent.validDateValidator]],
      onDateEnd: [null, [Validators.required, DialogCalendarEventComponent.validDateValidator]],
      isWholeday: [false],
      startTimeZone: [this.selectedZone],
      repeatType: ['never', [arrayValidator(this.repeatTypeOptions.map(item => item.id))]],
      repeatEvery: [1],
      repeatEndType: ['never', [arrayValidator(this.repeatEndOptions.map(item => item.id))]],
      repeatDaysUntil: [null],
      repeatDaysCount: [null],
      repeatOn: [['MO']],
      monthlyRepeatMode: [true],
      monthlyRepeatDay: [14],
      monthlyOrdinal: ['1'],
      monthlyWeekday: ['MO'],
      repeatMonth: [new Date().getMonth() + 1],
      maxParticipants: [null],
      shouldNotify: [true],
      creatorNotifications: [true],
      notifyBeforeMinutes: [this.calendarStore.schedulerOptions.defaultNotificationTime()],
      notifyAt: [null]
    });
  }

  private initializeFormDefaults(): void {
    const startAndEndTime = getEndDateTimeOneHourLater();
    this.attachmentFiles = [];
    this.participantsAllUsers = false;
    this.participantsNotifyAllUsers = false;
    const selectedCalendarId = this.calendarEvent?.calendar_id ?? this.calendarStore.selectedCalendarID() ?? this.availableCalendars()[0]?.id ?? null;

    this.calendarEventForm.patchValue({
      calendar_id: selectedCalendarId,
      calendar_name_display: this.getCalendarNameById(selectedCalendarId),
      onDateStart: startAndEndTime.start,
      onDateEnd: startAndEndTime.end,
      isWholeday: false,
      shouldNotify: true,
      creatorNotifications: true,
      notifyBeforeMinutes: this.calendarStore.schedulerOptions.defaultNotificationTime(),
      notifyAt: null
    });
    this.selectedCalendarId.set(selectedCalendarId);

    this.setCalendarSelectionState();

    this.applyNotificationValidators();
  }

  //PROCESSING FORM DATA
  private populateForm(event: CalendarEventResource): void {
    const recurrenceRule = decodeRrule(event.recurrence.rrule ?? '');
    const shouldNotify = event.timing.is_full_day
      ? event.timing.notify_time !== null
      : event.timing.notify_before !== null;
    const isOverrideEvent = event.override.parent_id !== null;

    this.selectedCalendarId.set(event.calendar_id);

    this.calendarEventForm.patchValue({
      calendar_id: event.calendar_id,
      calendar_name_display: this.getCalendarNameById(event.calendar_id),
      name: event.name,
      priority: event.priority,
      description: event.description ?? '',
      onDateStart: new Date(event.timing.start_at),
      onDateEnd: endTimeCalculation(new Date(event.timing.start_at), event.timing.duration, event.timing.is_full_day),
      isWholeday: event.timing.is_full_day,
      notifyAt: (event.timing.notify_time !== null) ? formatTimeAndMinuteToDate(new Date(event.timing.start_at), event.timing.notify_time) : null,
      startTimeZone: event.timing.timezone || this.selectedZone,
      repeatType: isOverrideEvent ? 'never' : recurrenceRule.repeatType,
      repeatEvery: isOverrideEvent ? 1 : recurrenceRule.repeatEvery,
      repeatEndType: isOverrideEvent ? 'never' : recurrenceRule.repeatEndType ?? 'never',
      repeatDaysUntil: isOverrideEvent ? null : recurrenceRule.repeatDaysUntil,
      repeatDaysCount: isOverrideEvent ? null : recurrenceRule.repeatDaysCount,
      repeatOn: isOverrideEvent ? ['MO'] : recurrenceRule.repeatOn,
      monthlyRepeatMode: isOverrideEvent ? true : recurrenceRule.monthlyRepeatMode,
      monthlyRepeatDay: isOverrideEvent ? 14 : recurrenceRule.monthlyRepeatDay,
      monthlyOrdinal: recurrenceRule.monthlyOrdinal ?? '1',
      monthlyWeekday: recurrenceRule.monthlyWeekday ?? 'MO',
      repeatMonth: recurrenceRule.repeatMonth ?? new Date().getMonth() + 1,
      maxParticipants: event.capacity,
      enableCustomLocation: event.location !== null && (event.location as unknown as string)?.length > 0,
      location: event.location,
      shouldNotify: shouldNotify,
      creatorNotifications: event.creator.notifications ?? true,
      notifyBeforeMinutes: event.timing.notify_before === null ? 1 : event.timing.notify_before
    }, {emitEvent: false});
    this.setCalendarSelectionState();
    this.applyNotificationValidators();
    this.participantsUserIDs = event.participants.user_ids ?? [];
    this.participantsGroupIDs = event.participants.group_ids ?? [];
    this.pushUserIDs = event.participants.notify_user_ids ?? [];
    this.pushGroupIDs = event.participants.notify_group_ids ?? [];
    this.participantsMails = event.participants.external_user_emails ?? [];
    this.participantsAllUsers = !!event.participants.all_users;
    this.participantsNotifyAllUsers = !!event.participants.notify_all_users;
    this.attachmentFiles = [...(event.files ?? [])];
    this.applySelectedInventories(event.inventories ?? []);
  }

  private subscribeToFormChanges(): void {
    const form = this.calendarEventForm;

    //update onDateEnd when onDateStart changes
    form.get('onDateStart')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(startDate => {
        if (this.suppressFormChangeReactions || !this.shouldReactToUserChange('onDateStart')) {
          return;
        }
        if(startDate !== null) {
          const endDateControl = form.get('onDateEnd');
          const allDayControl = form.get('isWholeday');
          if(allDayControl?.value) {
            if(endDateControl){
              const endDateNew = new Date(startDate);
              endDateNew.setHours(23, 59, 0, 0);
              endDateControl.setValue(endDateNew, {emitEvent: false});
            }
          } else {
            if (endDateControl) {
              const endDateNew = getEndDateTimeOneHourLater(startDate);
              endDateControl.setValue(endDateNew.end);
            }
          }
        }
      });

    form.get('calendar_id')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((calendarId: number | null) => {
        this.selectedCalendarId.set(calendarId);
      });

    form.get('onDateEnd')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(endDate => {
        if (this.suppressFormChangeReactions || !this.shouldReactToUserChange('onDateEnd')) {
          return;
        }
        if(endDate !== null) {
          const startDateControl = form.get('onDateStart');
          const allDayControl = form.get('isWholeday');
          if(startDateControl && allDayControl) {
            if(allDayControl.value) {
              if(allDayControl.value) {
                const adjustedEnd = new Date(endDate);
                if (adjustedEnd < startDateControl.value) {
                  adjustedEnd.setTime(startDateControl.value.getTime());
                }
                adjustedEnd.setHours(23, 59, 0, 0);
                form.get('onDateEnd')?.setValue(adjustedEnd, {emitEvent: false});
              }
              } else {
              // For recurring non-all-day events, limit duration to max 1439 minutes (23h 59m)
              const repeatType = form.get('repeatType')?.value;
              if (repeatType && repeatType !== 'never') {
                const duration = (endDate.getTime() - startDateControl.value.getTime()) / 60000;
                if (duration > 1439) {
                const maxEndDate = new Date(startDateControl.value.getTime() + 1439 * 60000);
                form.get('onDateEnd')?.setValue(maxEndDate, {emitEvent: false});
                return;
                }
              }
              if(startDateControl.value.getTime() >= endDate.getTime()) {
                const endDateNew = new Date(startDateControl.value.getTime() + 30 * 60000);
                form.get('onDateEnd')?.setValue(endDateNew);
              }
            }
          }
        }
      });

    //clear timezones if whole day
    form.get('isWholeday')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAllDay => {
        if (this.suppressFormChangeReactions || !this.shouldReactToUserChange('isWholeday')) {
          return;
        }
        this.applyNotificationValidators();
        if (isAllDay) {
            const currentStart = form.get('onDateStart')?.value;
            const currentEnd = form.get('onDateEnd')?.value;
            if (currentStart && currentEnd) {
              this.previousTimedRange = {
                start: new Date(currentStart),
                end: new Date(currentEnd)
              };
            }
            if (currentStart) {
              const newStart = new Date(currentStart);
              newStart.setHours(0, 0, 0, 0);
              const newEnd = new Date(currentStart);
              newEnd.setHours(23, 59, 0, 0);
              this.runWithoutFormChangeReactions(() => {
                form.patchValue(
                  {
                    onDateStart: newStart,
                    onDateEnd: newEnd
                  },
                  {emitEvent: true});
                form.patchValue(
                  {
                    onDateStart: newStart
                  },
                  {emitEvent: true});
              });
            } else {
              this.runWithoutFormChangeReactions(() => {
                form.patchValue(
                  {
                    notifyAt: null
                  },
                  {emitEvent: true});
              });
            }
        } else {
          if (this.previousTimedRange) {
            const previousTimedRange = this.previousTimedRange;
            this.runWithoutFormChangeReactions(() => {
              form.patchValue(
                {
                  onDateStart: new Date(previousTimedRange.start),
                  onDateEnd: new Date(previousTimedRange.end)
                },
                {emitEvent: true}
              );
            });
          }
        }
      });

    form.get('shouldNotify')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(shouldNotify => {
        this.applyNotificationValidators();
        if (!shouldNotify) {
          this.participantsNotifyAllUsers = false;
          this.pushUserIDs = [];
          this.pushGroupIDs = [];
          this.onParticipantsChange();
          form.get('notifyAt')?.updateValueAndValidity({emitEvent: false});
          form.get('notifyBeforeMinutes')?.updateValueAndValidity({emitEvent: false});
        }
      });

    form.get('calendar_id')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((calendarId) => {
        form.get('calendar_name_display')?.setValue(this.getCalendarNameById(calendarId), {emitEvent: false});
        this.ensureActiveStepInRange();
      });

    //repeat dropdown event
    form.get('repeatType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        if (!type) return;
        if (type === 'monthly' || type === 'yearly') {
          this.calendarEventForm.patchValue({monthlyRepeatMode: true}, {emitEvent: false});
          this.syncMonthlyRepeatDayWithStartDate(form.get('onDateStart')?.value);
        }
        if (type === 'never') {
          this.calendarEventForm.patchValue({repeatEndType: 'never'}, {emitEvent: false});
        }

        // Check if the selected type is one of the options which where should set the repeatEvery to 1
        const isRepeatable = this.repeatTypeOptions.some(opt => opt.id === type && type !== 'never');

        if (isRepeatable) {
          this.calendarEventForm.patchValue({repeatEvery: 1}, {emitEvent: true});

          // Limit duration to max 1439 minutes when switching to repeating event
          const startDateControl = form.get('onDateStart');
          const endDateControl = form.get('onDateEnd');
          const allDayControl = form.get('isWholeday');
          if (startDateControl?.value && endDateControl?.value && !allDayControl?.value) {
            const duration = (endDateControl.value.getTime() - startDateControl.value.getTime()) / 60000;
            if (duration > 1439) {
              const maxEndDate = new Date(startDateControl.value.getTime() + 1439 * 60000);
              endDateControl.setValue(maxEndDate, {emitEvent: false});
            }
          }
        }
        switch (type) {
          case 'daily':
            this.repeatUnit = this.languageService.getTranslationByKey('DAYS');
            break;
          case 'weekly':
            this.repeatUnit = this.languageService.getTranslationByKey('WEEKS');
            break;
          case 'monthly':
            this.repeatUnit = this.languageService.getTranslationByKey('MONTHS');
            break;
          case 'yearly':
            this.repeatUnit = this.languageService.getTranslationByKey('YEARS');
            break;
        }
      });

    //repeatEndType handling
    form.get('repeatEndType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        const until = form.get('repeatDaysUntil');
        const count = form.get('repeatDaysCount');

        until?.clearValidators();
        count?.clearValidators();

        if (type === 'until') {
          if (!until?.value) {
            const start = form.get('onDateStart')?.value || new Date();
            const defaultUntil = new Date(start);
            defaultUntil.setDate(defaultUntil.getDate() + 7);
            until?.setValue(defaultUntil);
          }
          until?.setValidators([Validators.required]);
        } else if (type === 'count') {
          if (!count?.value) {
            count?.setValue(10);
          }
          count?.setValidators([Validators.required, Validators.min(1)]);
        }

        until?.updateValueAndValidity();
        count?.updateValueAndValidity();
    });
  }

  private setupAsyncValidation(): void {
    this.setupRecurrenceValidation();
    this.setupInventoryValidation();
  }

  private captureInitialValidationBaseline(): void {
    this.initialRecurrenceValidationKey = this.buildRecurrenceValidationPayload(false)?.key ?? null;
    this.initialInventoryValidationKey = this.buildInventoryValidationPayload(false)?.key ?? null;
  }

  private setupRecurrenceValidation(): void {
    const recurrenceControls = [
      'onDateStart',
      'onDateEnd',
      'isWholeday',
      'startTimeZone',
      'repeatType',
      'repeatEvery',
      'repeatEndType',
      'repeatDaysUntil',
      'repeatDaysCount',
      'repeatOn',
      'monthlyRepeatMode',
      'monthlyRepeatDay',
      'monthlyOrdinal',
      'monthlyWeekday',
      'repeatMonth'
    ];

    recurrenceControls.forEach((controlName) => {
      this.calendarEventForm.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.queueRecurrenceValidation();
        });
    });

    this.recurrenceValidationTrigger$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(this.validationDebounceMs),
        map(() => this.buildRecurrenceValidationPayload()),
        switchMap((payload) => {
          if (!payload) {
            return of(null);
          }

          if (payload.key === this.initialRecurrenceValidationKey) {
            this.recurrenceValidation = this.createValidationState();
            return of(null);
          }

          this.recurrenceValidation = {
            status: 'pending',
            message: '',
            key: payload.key
          };

          return this.apiService.checkCalendarEventRecursion(payload.request).pipe(
            map((result) => ({
              key: payload.key,
              status: result.valid ? 'success' : 'error' as AsyncValidationStatus,
              message: result.message ?? this.languageService.getTranslationByKey('UNKNOWNRRULEVALIDATIONERROR')
            })),
            catchError(() => of({
              key: payload.key,
              status: 'error' as AsyncValidationStatus,
              message: this.languageService.getTranslationByKey('INVALIDINPUT')
            }))
          );
        })
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.recurrenceValidation = result;
      });
  }

  private syncMonthlyRepeatDayWithStartDate(startDate: Date | null | undefined): void {
    if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) {
      return;
    }

    this.calendarEventForm.get('monthlyRepeatDay')?.setValue(startDate.getDate(), {emitEvent: false});
  }

  private setupInventoryValidation(): void {
    const inventoryRelatedControls = [
      'calendar_id',
      'onDateStart',
      'onDateEnd',
      'isWholeday',
      'startTimeZone',
      'repeatType',
      'repeatEvery',
      'repeatEndType',
      'repeatDaysUntil',
      'repeatDaysCount',
      'repeatOn',
      'monthlyRepeatMode',
      'monthlyRepeatDay',
      'monthlyOrdinal',
      'monthlyWeekday',
      'repeatMonth',
      'maxParticipants'
    ];

    inventoryRelatedControls.forEach((controlName) => {
      this.calendarEventForm.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.inventoryValidationTrigger$.next();
        });
    });

    this.inventoryValidationTrigger$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(this.validationDebounceMs),
        map(() => this.buildInventoryValidationPayload()),
        switchMap((payload) => {
          if (!payload) {
            return of(null);
          }

          if (payload.key === this.initialInventoryValidationKey) {
            this.inventoryValidation = this.createInventoryValidationState();
            return of(null);
          }

          this.inventoryValidation = {
            status: 'pending',
            message: '',
            key: payload.key,
            inventoryMessages: {},
            inventoryFailures: {},
            hasBlockingFailure: false
          };

          return this.apiService.checkCalendarEventInventories(payload.request).pipe(
            map((result) => ({
              key: payload.key,
              status: result.some((item) => item.failure) ? 'error' : 'success' as AsyncValidationStatus,
              message: this.getInventoryValidationMessage(result),
              inventoryMessages: this.getInventoryValidationMessagesByInventory(result),
              inventoryFailures: this.getInventoryValidationFailuresByInventory(result),
              hasBlockingFailure: result.some((item) => item.failure)
            })),
            catchError(() => of({
              key: payload.key,
              status: 'error' as AsyncValidationStatus,
              message: this.languageService.getTranslationByKey('INVALIDINPUT'),
              inventoryMessages: {},
              inventoryFailures: {},
              hasBlockingFailure: true
            }))
          );
        })
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.inventoryValidation = result;
      });
  }

  private queueRecurrenceValidation(): void {
    if (this.suppressFormChangeReactions) {
      return;
    }

    this.recurrenceValidationTrigger$.next();
  }

  private setCalendarSelectionState(): void {
    const calendarControl = this.calendarEventForm.get('calendar_id');
    const calendarNameDisplayControl = this.calendarEventForm.get('calendar_name_display');
    if (!calendarControl) {
      return;
    }

    if (!this.canEditCalendarSelection && this.calendarEvent?.calendar_id !== null && this.calendarEvent?.calendar_id !== undefined) {
      calendarControl.setValue(this.calendarEvent.calendar_id, {emitEvent: false});
      calendarControl.disable({emitEvent: false});
    } else {
      calendarControl.enable({emitEvent: false});
    }

    calendarNameDisplayControl?.setValue(this.selectedCalendarName, {emitEvent: false});
    calendarNameDisplayControl?.disable({emitEvent: false});
  }

  private getCalendarNameById(calendarId: number | string | null | undefined): string {
    if (calendarId === null || calendarId === undefined) {
      return '';
    }

    return this.availableCalendars().find((calendar) => calendar.id === calendarId)?.name ?? '';
  }

  private runWithoutFormChangeReactions(callback: () => void): void {
    this.suppressFormChangeReactions = true;
    try {
      callback();
    } finally {
      this.suppressFormChangeReactions = false;
    }
  }

  private shouldReactToUserChange(controlName: string): boolean {
    return this.calendarEventForm.get(controlName)?.dirty ?? false;
  }

  private createValidationState(): StepValidationState {
    return {
      status: 'idle',
      message: '',
      key: null
    };
  }

  private createInventoryValidationState(): InventoryValidationDisplayState {
    return {
      status: 'idle',
      message: '',
      key: null,
      inventoryMessages: {},
      inventoryFailures: {},
      hasBlockingFailure: false
    };
  }

  private buildRecurrenceValidationPayload(resetState: boolean = true): {request: CalendarEventRRuleValidationRequest; key: string} | null {
    if (this.suppressFormChangeReactions || !this.isRecurrenceValidationLocallyValid() || !this.shouldValidateRecurrence()) {
      if (resetState) {
        this.recurrenceValidation = this.createValidationState();
      }
      return null;
    }

    const request: CalendarEventRRuleValidationRequest = {
      start_at: this.calendarEventForm.controls['onDateStart'].value?.toISOString(),
      timezone: this.calendarEventForm.controls['startTimeZone'].value,
      duration: this.calculateEventDuration(
        this.calendarEventForm.controls['onDateStart'].value,
        this.calendarEventForm.controls['onDateEnd'].value,
        this.calendarEventForm.controls['isWholeday'].value
      ),
      rrule: this.getCurrentRecurrenceRule()
    };

    return {
      request,
      key: JSON.stringify(request)
    };
  }

  private buildInventoryValidationPayload(resetState: boolean = true): {request: CalendarEventInventoryValidationRequest; key: string} | null {
    if (this.suppressFormChangeReactions || !this.shouldShowResourcesStep || !this.isResourcesStepLocallyValid() || !this.isDateStepLocallyValid()) {
      if (resetState) {
        this.inventoryValidation = this.createInventoryValidationState();
      }
      return null;
    }

    const selectedInventories = this.getSelectedInventories();
    const request: CalendarEventInventoryValidationRequest = {
      calendar_id: this.canEditCalendarSelection
        ? (this.calendarEventForm.controls['calendar_id'].value ?? this.calendarStore.selectedCalendarID() ?? -1)
        : (this.calendarEvent?.calendar_id ?? this.calendarEventForm.controls['calendar_id'].value ?? this.calendarStore.selectedCalendarID() ?? -1),
      calendar_event_id: this.calendarEvent?.id && this.calendarEvent.id !== -1 ? this.calendarEvent.id : null,
      capacity: selectedInventories.length > 0 ? this.calendarEventForm.controls['maxParticipants'].value : null,
      start_at: this.calendarEventForm.controls['onDateStart'].value?.toISOString(),
      timezone: this.calendarEventForm.controls['startTimeZone'].value,
      duration: this.calculateEventDuration(
        this.calendarEventForm.controls['onDateStart'].value,
        this.calendarEventForm.controls['onDateEnd'].value,
        this.calendarEventForm.controls['isWholeday'].value
      ),
      rrule: this.isOverrideEvent ? null : this.getCurrentRecurrenceRule(),
      inventories: selectedInventories.map((item) => ({
        id: item.inventory.id,
        quantity: item.quantity
      }))
    };

    return {
      request,
      key: JSON.stringify(request)
    };
  }

  private isInventoryValidationUnsupported(): boolean {
    return this.selectedCalendarConfig()?.inventory_mode === 'deny';
  }

  private getCurrentRecurrenceRule(): string {
    return this.isOverrideEvent ? '' : buildRecurrenceString({
      repeatType: this.calendarEventForm.controls['repeatType'].value,
      repeatEvery: this.calendarEventForm.controls['repeatEvery'].value,
      repeatEndType: this.calendarEventForm.controls['repeatEndType'].value,
      repeatDaysUntil: this.calendarEventForm.controls['repeatDaysUntil'].value,
      repeatDaysCount: this.calendarEventForm.controls['repeatDaysCount'].value,
      repeatOn: this.calendarEventForm.controls['repeatOn'].value,
      monthlyRepeatMode: this.calendarEventForm.controls['monthlyRepeatMode'].value,
      monthlyRepeatDay: this.calendarEventForm.controls['monthlyRepeatDay'].value,
      monthlyOrdinal: this.calendarEventForm.controls['monthlyOrdinal'].value,
      monthlyWeekday: this.calendarEventForm.controls['monthlyWeekday'].value,
      repeatMonth: this.calendarEventForm.controls['repeatMonth'].value,
    });
  }

  private shouldValidateRecurrence(): boolean {
    return !this.isOverrideEvent && this.calendarEventForm.controls['repeatType'].value !== 'never';
  }

  private isDateStepLocallyValid(): boolean {
    return this.areStepControlsValid(1, false);
  }

  private isRecurrenceValidationLocallyValid(): boolean {
    return this.areNamedControlsValid([
      'onDateStart',
      'onDateEnd',
      'isWholeday',
      'startTimeZone',
      'repeatType',
      'repeatEvery',
      'repeatEndType',
      'repeatDaysUntil',
      'repeatDaysCount',
      'repeatOn',
      'monthlyRepeatMode',
      'monthlyRepeatDay',
      'monthlyOrdinal',
      'monthlyWeekday',
      'repeatMonth'
    ], false);
  }

  private isResourcesStepLocallyValid(): boolean {
    return this.areStepControlsValid(3, false);
  }

  public isDateStepServerValid(showToaster: boolean = false): boolean {
    if (!this.shouldValidateRecurrence()) {
      return true;
    }

    const payload = this.buildRecurrenceValidationPayload();
    if (payload?.key === this.initialRecurrenceValidationKey) {
      return true;
    }

    if (!payload) {
      if (showToaster && this.recurrenceValidation.message) {
        this.showStepValidationError(this.recurrenceValidation.message);
      }
      return false;
    }

    const isValid = this.recurrenceValidation.status === 'success' && this.recurrenceValidation.key === payload.key;
    if (!isValid && showToaster && this.recurrenceValidation.message) {
      this.showStepValidationError(this.recurrenceValidation.message);
    }

    return isValid;
  }

  public isResourcesStepServerValid(showToaster: boolean = false): boolean {
    if (!this.shouldShowResourcesStep) {
      return true;
    }

    const payload = this.buildInventoryValidationPayload();
    if (payload?.key === this.initialInventoryValidationKey) {
      return true;
    }

    if (!payload) {
      if (showToaster && this.inventoryValidation.message) {
        this.showStepValidationError(this.inventoryValidation.message);
      }
      return false;
    }

    const isValid = this.inventoryValidation.key === payload.key && !this.inventoryValidation.hasBlockingFailure;
    if (!isValid && showToaster && this.inventoryValidation.message) {
      this.showStepValidationError(this.inventoryValidation.message);
    }

    return isValid;
  }

  public canAdvanceFromActiveStep(): boolean {
    if (this.steps[this.activeStep]?.id === 'date') {
      return this.isDateStepServerValid();
    }

    if (this.steps[this.activeStep]?.id === 'resources') {
      return this.isResourcesStepServerValid();
    }

    return true;
  }

  public canSaveCurrentEvent(): boolean {
    if (!this.isDateStepServerValid()) {
      return false;
    }

    if (this.shouldShowResourcesStep && !this.isResourcesStepServerValid()) {
      return false;
    }

    return true;
  }

  public getRecurrenceValidationMessage(): string {
    return this.recurrenceValidation.message;
  }

  public getInventoryItemValidationMessage(inventoryId: number): string {
    return this.inventoryValidation.inventoryMessages[inventoryId] ?? '';
  }

  public shouldShowInventoryItemValidationAsError(inventoryId: number): boolean {
    return this.inventoryValidation.inventoryFailures[inventoryId] ?? false;
  }

  private getInventoryValidationMessage(results: CalendarEventInventoryValidationResource[]): string {
    const genericMessage = results.find((item) => item.message != null);
    if (!genericMessage) {
      return '';
    }

    return genericMessage.message ?? '';
  }

  private getInventoryValidationMessagesByInventory(results: CalendarEventInventoryValidationResource[]): Record<number, string> {
    return results.reduce<Record<number, string>>((messages, result) => {
      if (!result.inventory?.id || (!result.failure && !result.error && !result.message)) {
        return messages;
      }

      messages[result.inventory.id] = result.message ?? this.languageService.getTranslationByKey('INVALIDINPUT');

      return messages;
    }, {});
  }

  private getInventoryValidationFailuresByInventory(results: CalendarEventInventoryValidationResource[]): Record<number, boolean> {
    return results.reduce<Record<number, boolean>>((failures, result) => {
      if (!result.inventory?.id) {
        return failures;
      }

      failures[result.inventory.id] = result.failure;
      return failures;
    }, {});
  }

  private showStepValidationError(message: string): void {
    this.toasterService.error(
      this.languageService.getTranslationByKey('RECCURENCEERROR'),
      message
    );
  }

  private applyNotificationValidators(): void {
  const isWholeDay = this.calendarEventForm.get('isWholeday')?.value;
  const shouldNotify = this.calendarEventForm.get('shouldNotify')?.value;
  const notifyBeforeMinutesControl = this.calendarEventForm.get('notifyBeforeMinutes');
  const notifyAtControl = this.calendarEventForm.get('notifyAt');

  notifyBeforeMinutesControl?.clearValidators();
  notifyAtControl?.clearValidators();

  if (shouldNotify) {
    if (isWholeDay) {
      notifyAtControl?.setValidators([Validators.required]);
    } else {
      notifyBeforeMinutesControl?.setValidators([Validators.required, Validators.min(1)]);
    }
  }

  notifyBeforeMinutesControl?.updateValueAndValidity({emitEvent: false});
  notifyAtControl?.updateValueAndValidity({emitEvent: false});
}

  private setupSectionEditTracking(): void {
    const form = this.calendarEventForm;
    
    // Section 0: Basic details (name, priority, description, location)
    const section0Controls = ['calendar_id', 'name', 'priority', 'description', 'location'];
    section0Controls.forEach(controlName => {
      form.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.section0Edited = true;
        });
    });
    
    // Section 1: Date/time and recurrence settings
    const section1Controls = [
      'onDateStart', 'onDateEnd', 'isWholeday', 'startTimeZone',
      'repeatType', 'repeatEvery', 'repeatEndType', 'repeatDaysUntil', 'repeatDaysCount',
      'repeatOn', 'monthlyRepeatMode', 'monthlyRepeatDay', 'monthlyOrdinal',
      'monthlyWeekday', 'repeatMonth', 'shouldNotify', 'notifyBeforeMinutes', 'notifyAt'
    ];
    section1Controls.forEach(controlName => {
      form.get(controlName)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.section1Edited = true;
        });
    });
    
    // Section 2: Participants - track array changes
    // Note: Since participants are bound with [(user)], [(groups)], etc., 
    // we'll track changes through property setters or direct monitoring
  }

  private rebuildInventoryForm: EffectRef = effect(() => {
    const controls: Record<string, AbstractControl> = {};

    controls['selectedResourceIds'] = this.formBuilder.nonNullable.control<number[]>([]);

    this.availableInventories().forEach((inventory) => {
      controls[this.getInventoryQuantityControlName(inventory.id)] = this.formBuilder.control<number | null>(1);
    });

    this.inventoryForm = this.formBuilder.group(controls);
    this.selectedInventoryOptions = this.availableInventories().map((inventory) => ({
      id: inventory.id,
      name: inventory.name
    }));

    this.inventoryForm.get('selectedResourceIds')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedResourceIds) => {
        this.syncSelectedInventories(Array.isArray(selectedResourceIds) ? selectedResourceIds : []);
        this.calculateEventCapacity();
        this.section3Edited = true;
        this.inventoryValidationTrigger$.next();
      });

    this.availableInventories().forEach((inventory) => {
      this.inventoryForm.get(this.getInventoryQuantityControlName(inventory.id))?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.calculateEventCapacity();
          this.section3Edited = true;
          this.inventoryValidationTrigger$.next();
        });

      this.updateInventoryControlStates(inventory, false);
    });

    if (this.calendarEvent?.inventories?.length && this.availableInventories().length > 0) {
      this.applySelectedInventories(this.calendarEvent.inventories);
    }

    if (!this.section3Edited) {
      this.initialInventoryValidationKey = this.buildInventoryValidationPayload(false)?.key ?? null;
    }

  });

  private applySelectedInventories(inventories: CalendarEventInventoryResource[]): void {
    if (Object.keys(this.inventoryForm.controls).length === 0) {
      return;
    }

    const inventoryMap = new Map(inventories.map((item) => [item.inventory.id, item]));
    const selectedResourceIds = inventories.map((item) => item.inventory.id);

    this.availableInventories().forEach((inventory) => {
      const selectedInventory = inventoryMap.get(inventory.id);
      const quantity = this.getSanitizedInventoryQuantity(
        inventory,
        selectedInventory?.quantity ?? 1
      );

      this.inventoryForm.patchValue({
        selectedResourceIds,
        [this.getInventoryQuantityControlName(inventory.id)]: quantity,
      }, {emitEvent: false});
    });

    this.syncSelectedInventories(selectedResourceIds, false);
    this.calculatedEventCapacity = this.getCalculatedEventCapacity();

    Object.values(this.inventoryForm.controls).forEach((control) => {
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  private updateInventoryControlStates(inventory: CalendarInventoryResource, selected: boolean): void {
    const quantityControl = this.inventoryForm.get(this.getInventoryQuantityControlName(inventory.id));
    if (!quantityControl) {
      return;
    }

    if (selected && this.shouldRequireInventoryQuantity(inventory)) {
      quantityControl.setValidators([Validators.required, Validators.min(1)]);
      quantityControl.enable({emitEvent: false});
      quantityControl.setValue(this.getSanitizedInventoryQuantity(inventory, quantityControl.value), {emitEvent: false});
    } else {
      quantityControl.clearValidators();
      quantityControl.setValue(this.getSanitizedInventoryQuantity(inventory, 1), {emitEvent: false});
      quantityControl.disable({emitEvent: false});
    }

    quantityControl.updateValueAndValidity({emitEvent: false});
  }

  private syncSelectedInventories(selectedResourceIds: number[], markControlsPristine: boolean = true): void {
    this.availableInventories().forEach((inventory) => {
      const isSelected = selectedResourceIds.includes(inventory.id);
      this.updateInventoryControlStates(inventory, isSelected);

      if (!markControlsPristine) {
        return;
      }

      this.inventoryForm.get(this.getInventoryQuantityControlName(inventory.id))?.markAsPristine();
    });
  }

  private getSelectedInventories(): CalendarEventInventoryResource[] {
    return this.getSelectedInventoriesForDisplay()
      .map((inventory) => ({
        inventory,
        quantity: this.shouldRequireInventoryQuantity(inventory)
          ? this.getSanitizedInventoryQuantity(
            inventory,
            this.inventoryForm.get(this.getInventoryQuantityControlName(inventory.id))?.value
          )
          : null
      }));
  }


  private shouldRequireInventoryQuantity(inventory: CalendarInventoryResource): boolean {
    return inventory.conflict_mode !== CalendarInventoryConflictMode.SHARED;
  }

  public shouldShowInventoryQuantity(inventory: CalendarInventoryResource): boolean {
    return this.shouldRequireInventoryQuantity(inventory);
  }

  public shouldShowInventoryCapacity(inventory: CalendarInventoryResource): boolean {
    return inventory.capacity_mode !== CalendarInventoryCapacityMode.IRRELEVANT && this.getEffectiveInventoryCapacity(inventory) !== null;
  }

  public isInventoryCapacityChangeable(inventory: CalendarInventoryResource): boolean {
    return inventory.capacity_mode === CalendarInventoryCapacityMode.ALTERABLE && this.isInventorySelected(inventory.id);
  }

  public getInventoryCapacityModeLabel(inventory: CalendarInventoryResource): string {
    switch (inventory.capacity_mode) {
      case CalendarInventoryCapacityMode.FIXED:
        return this.languageService.getTranslationByKey('RESOURCECAPACITYMODEFIXED');
      case CalendarInventoryCapacityMode.ALTERABLE:
        return this.languageService.getTranslationByKey('RESOURCECAPACITYMODEALTERABLE');
      default:
        return this.languageService.getTranslationByKey('RESOURCECAPACITYMODEEIRRELEVANT');
    }
  }

  public getInventoryQuantityModeLabel(inventory: CalendarInventoryResource): string {
    switch (inventory.conflict_mode) {
      case CalendarInventoryConflictMode.EXCLUSIVE:
        return this.languageService.getTranslationByKey('RESOURCEQUANTITYMODEEXCLUSIVE');
      case CalendarInventoryConflictMode.WARNING:
        return this.languageService.getTranslationByKey('RESOURCEQUANTITYMODEWARNING');
      default:
        return this.languageService.getTranslationByKey('RESOURCEQUANTITYMODESHARED');
    }
  }

  public getInventoryAvailableQuantity(inventory: CalendarInventoryResource): number {
    return Math.max(1, inventory.quantity ?? 1);
  }

  public getInventoryQuantityMaximum(inventory: CalendarInventoryResource): number|null {
    return inventory.conflict_mode === CalendarInventoryConflictMode.EXCLUSIVE ? inventory.quantity : null
  }

  public isInventorySelected(inventoryId: number): boolean {
    const selectedResourceIds = this.inventoryForm.get('selectedResourceIds')?.value;
    return Array.isArray(selectedResourceIds) && selectedResourceIds.includes(inventoryId);
  }

  public getSelectedInventoriesForDisplay(): CalendarInventoryResource[] {
    const selectedResourceIds = this.inventoryForm.get('selectedResourceIds')?.value;
    if (!Array.isArray(selectedResourceIds)) {
      return [];
    }

    return selectedResourceIds
      .map((id) => this.availableInventories().find((inventory) => inventory.id === id))
      .filter((inventory): inventory is CalendarInventoryResource => !!inventory)
  }

  private calculateEventCapacity(): void {
    this.calculatedEventCapacity = this.getCalculatedEventCapacity();
    this.calendarEventForm.controls['maxParticipants'].setValue(this.calculatedEventCapacity, {emitEvent: false});
  }

  private getCalculatedEventCapacity(): number | null {
    const selectedInventories = this.getSelectedInventoriesForDisplay();

    if(selectedInventories.every((item) => item.capacity === null)) {
      return null;
    }

    let totalCapacity = 0;

    selectedInventories.forEach((item) => {
      const capacity = this.getEffectiveInventoryCapacity(item);
      if (capacity !== null) {
        const quantity = this.shouldRequireInventoryQuantity(item)
          ? this.getSanitizedInventoryQuantity(
            item,
            this.inventoryForm.get(this.getInventoryQuantityControlName(item.id))?.value
          )
          : 1;
    
        totalCapacity += quantity * capacity;
      } 
    });

    return totalCapacity;
  }

  public getEffectiveInventoryCapacity(inventory: CalendarInventoryResource): number | null {
    if (inventory.capacity_mode === CalendarInventoryCapacityMode.IRRELEVANT || inventory.capacity === null) {
      return null;
    }
    return inventory.capacity;
  }

  private getSanitizedInventoryQuantity(inventory: CalendarInventoryResource, quantity: number | null | undefined): number {
    const normalizedQuantity = quantity ?? 1;
    return Math.max(normalizedQuantity, 1);
  }

  private getSanitizedInventoryCapacity(inventory: CalendarInventoryResource, capacity: number | null | undefined): number {
    const baseCapacity = inventory.capacity ?? 1;
    const normalizedCapacity = capacity ?? baseCapacity;
    return Math.max(normalizedCapacity, 1);
  }

  private getInventoryQuantityControlName(inventoryId: number): string {
    return `inventory_quantity_${inventoryId}`;
  }

  public toggleRepeatDay(dayCode: string): void {
    const control = this.calendarEventForm.get('repeatOn');
    if (!control) return;

    const current = control.value || [];

    if (current.includes(dayCode)) {
      control.setValue(current.filter((d: string) => d !== dayCode));
    } else {
      control.setValue([...current, dayCode]);
    }
  }

  //based on the language we translate the dropdown values with appropritate language based names
  private setLocalizedOptions(): void {
    const lang = this.languageService.getLanguage();
    const baseDate = new Date(Date.UTC(2024, 0, 1)); // Jan 1, 2024 = Monday is 100% a year with 1 Jan as monday

    const formatterShort = new Intl.DateTimeFormat(lang, {weekday: 'narrow'});
    const formatterLong = new Intl.DateTimeFormat(lang, {weekday: 'long'});

    const dayCodes = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    const formatterMonth = new Intl.DateTimeFormat(lang, {month: 'long'});

    this.weekdayOptions = dayCodes.map((code, i) => ({
      value: code,
      label: formatterShort.format(new Date(baseDate.getTime() + i * 86400000))
    }));

    this.weekdayOptionsLong = dayCodes.map((code, i) => ({
      id: code,
      name: formatterLong.format(new Date(baseDate.getTime() + i * 86400000))
    }));

    this.monthOptions = Array.from({length: 12}, (_, i) => ({
      id: i + 1,
      name: formatterMonth.format(new Date(2024, i, 1))
    }));
  }
  //#endregion form handling

  //#region dialog handling
  public handleStepChange(targetStep: number): void {
    const isMovingForward = targetStep > this.activeStep;
    const currentStep = this.steps[this.activeStep];

    if (isMovingForward && !this.isCurrentStepValid()) {
      return;
    }

    if (currentStep?.id === 'date' && isMovingForward && !this.isDateStepServerValid(true)) {
      return;
    }

    if (currentStep?.id === 'resources' && isMovingForward && !this.isResourcesStepServerValid(true)) {
      return;
    }

    this.activeStep = targetStep;
  }

  public close(): void {
    this.visibleChange.emit(false);
  }

  public save(): void {
    if (!this.isEventNameValid()) {
      this.markNameControlAsInvalidInteraction();
      return;
    }

    if (this.calendarEventForm.invalid) {
      this.calendarEventForm.markAllAsTouched();
      return;
    }

    if (!this.isInventorySelectionValid()) {
      return;
    }

    if (!this.canSaveCurrentEvent()) {
      return;
    }

    const isEditingMaster: boolean = !!this.calendarEvent?.recurrence.rrule && !this.calendarEvent?.override.parent_id;
    const isNewOverrideEvent: boolean = (!this.calendarEvent?.id || this.calendarEvent?.id === -1) && this.calendarEvent?.override.parent_id !== null;
    const isNewEvent: boolean = (!this.calendarEvent?.id || this.calendarEvent?.id === -1) && !this.calendarEvent?.override.parent_id;
    
    const newEvent = this.mapDefaultParamsFromForm();
    if (isNewEvent || isNewOverrideEvent) {
      this.calendarStore.createEvent(newEvent);
      this.visibleChange.emit(false);
    } else {
      if(isEditingMaster){
        if(this.calendarEvent!.recurrence.deleted_occurrences.length > 0 || this.calendarEvent!.recurrence.overridden_occurrences.length > 0){
          this.eventToUpdate = newEvent;
          this.initializeAdaptCheckboxes();
          this.visibleQuickDialog = true;
        } else {
          this.calendarStore.updateEvent(newEvent, this.calendarEvent!.id!, false, false, false, false, this.calendarEvent!.calendar_id ?? newEvent.calendar_id);
          this.visibleChange.emit(false);
        }
      } else {
        // handles editing of existing override events
        this.calendarStore.updateEvent(newEvent, this.calendarEvent!.id!, false, false, false, false, this.calendarEvent!.calendar_id ?? newEvent.calendar_id);
        this.visibleChange.emit(false);
      }
    }

  }
  //#endregion dialog handling

  //#region override alignment dialog handling
  private initializeAdaptCheckboxes(): void {
    this.adaptDetails = this.section0Edited;
    this.adaptDateLocation = this.section1Edited;
    this.adaptGuests = this.section2Edited;
    this.adaptInventories = this.section3Edited;
  }

  public get allAdaptChecked(): boolean {
    const editedSections = [
      this.section0Edited && this.adaptDetails,
      this.section1Edited && this.adaptDateLocation,
      this.section2Edited && this.adaptGuests,
      this.section3Edited && this.adaptInventories
    ];
    const enabledSections = [this.section0Edited, this.section1Edited, this.section2Edited, this.section3Edited];
    const checkedCount = editedSections.filter(Boolean).length;
    const enabledCount = enabledSections.filter(Boolean).length;
    return enabledCount > 0 && checkedCount === enabledCount;
  }

  public set allAdaptChecked(value: boolean) {
    if (this.section0Edited) this.adaptDetails = value;
    if (this.section1Edited) this.adaptDateLocation = value;
    if (this.section2Edited) this.adaptGuests = value;
    if (this.section3Edited) this.adaptInventories = value;
  }

  public saveQuickDialog(): void {
    if(this.eventToUpdate !== null) {
      this.calendarStore.updateEvent(
        this.eventToUpdate, 
        this.calendarEvent!.id!, 
        this.adaptDetails, 
        this.adaptGuests,
        this.adaptDateLocation,
        this.adaptInventories,
        this.calendarEvent!.calendar_id ?? this.eventToUpdate.calendar_id
      );
    }
    this.eventToUpdate = null;
    this.visibleQuickDialog = false;
    this.visibleChange.emit(false);
  }

  public cancelQuickDialog(): void {
    this.visibleQuickDialog = false;
  }
  //#endregion override alignment dialog handling

  private mapDefaultParamsFromForm(): CalendarEventUpdateRequest {
    const defaultUpdateEvent: CalendarEventUpdateRequest = createCalendarEventUpdateRequest();
    defaultUpdateEvent.name = this.calendarEventForm.controls['name'].value;
    const description = this.calendarEventForm.controls['description'].value;
    const enableCustomLocation = this.calendarEventForm.controls['enableCustomLocation'].value;
    defaultUpdateEvent.description = typeof description === 'string' && description.trim() !== '' ? description : null;
    defaultUpdateEvent.location = enableCustomLocation ? this.calendarEventForm.controls['location'].value : null;
    defaultUpdateEvent.priority = this.calendarEventForm.controls['priority'].value;
    defaultUpdateEvent.calendar_id = this.canEditCalendarSelection
      ? (this.calendarEventForm.controls['calendar_id'].value ?? this.calendarStore.selectedCalendarID() ?? -1)
      : (this.calendarEvent?.calendar_id ?? this.calendarEventForm.controls['calendar_id'].value ?? this.calendarStore.selectedCalendarID() ?? -1);
    defaultUpdateEvent.creator.notifications = !!this.calendarEventForm.controls['creatorNotifications'].value;
    const shouldNotify = this.calendarEventForm.controls['shouldNotify'].value;
    defaultUpdateEvent.timing.notify_before = 
    (shouldNotify && this.calendarEventForm.controls['notifyBeforeMinutes'].value > 0 && !this.calendarEventForm.controls['isWholeday'].value) ?
      this.calendarEventForm.controls['notifyBeforeMinutes'].value :
      null;
    defaultUpdateEvent.timing.notify_time = (shouldNotify && this.calendarEventForm.controls['isWholeday'].value) ? 
      formatTimeIgnoringOffset(this.calendarEventForm.controls['notifyAt'].value) :
      null;
    defaultUpdateEvent.timing.timezone = this.calendarEventForm.controls['startTimeZone'].value;
    defaultUpdateEvent.timing.start_at = this.calendarEventForm.controls['onDateStart'].value;
    defaultUpdateEvent.timing.duration = this.calculateEventDuration(
      this.calendarEventForm.controls['onDateStart'].value,
      this.calendarEventForm.controls['onDateEnd'].value,
      this.calendarEventForm.controls['isWholeday'].value);
    defaultUpdateEvent.timing.is_full_day = this.calendarEventForm.controls['isWholeday'].value;
    defaultUpdateEvent.participants.user_ids = this.adjustUserIDs(this.participantsUserIDs);
    defaultUpdateEvent.participants.group_ids = this.participantsGroupIDs;
    defaultUpdateEvent.participants.external_user_emails = this.participantsMails;
    defaultUpdateEvent.participants.all_users = this.canInviteAllUsers() && this.participantsAllUsers;
    defaultUpdateEvent.participants.notify_all_users = this.canInviteAllUsers() && this.participantsNotifyAllUsers;
    defaultUpdateEvent.participants.notify_user_ids = this.pushUserIDs ?? [];
    defaultUpdateEvent.participants.notify_group_ids = this.pushGroupIDs ?? [];
    defaultUpdateEvent.file_storage_uuids = this.attachmentFiles.map((file) => file.uuid);
    const selectedInventories = this.getSelectedInventories();
    const isResourcesStepAvailable = this.steps.some((step) => step.id === this.resourcesStep.id);
    defaultUpdateEvent.capacity = isResourcesStepAvailable && selectedInventories.length > 0
      ? this.calendarEventForm.controls['maxParticipants'].value
      : null;
    defaultUpdateEvent.inventories = selectedInventories.map((item) => ({
      id: item.inventory.id,
      quantity: item.quantity
    }));
    defaultUpdateEvent.recurrence.rrule = this.getCurrentRecurrenceRule();

    // update the event with the existing data
    defaultUpdateEvent.recurrence.deleted_occurrences = this.calendarEvent?.recurrence.deleted_occurrences ?? [];
    defaultUpdateEvent.override.parent_id = this.calendarEvent?.override.parent_id ?? null;
    defaultUpdateEvent.override.original_at = this.calendarEvent?.override.original_at ?? null;
    return defaultUpdateEvent;
  }

  public onParticipantsAllUsersChange(value: boolean): void {
    if (!this.canInviteAllUsers()) {
      this.participantsAllUsers = false;
      this.participantsNotifyAllUsers = false;
      this.onParticipantsChange();
      return;
    }

    this.participantsAllUsers = value;
    if (value) {
      this.participantsUserIDs = [];
      this.participantsGroupIDs = [];
      this.participantsMails = [];
      this.participantsNotifyAllUsers = !!this.calendarEventForm.get('shouldNotify')?.value;
      if (this.participantsNotifyAllUsers) {
        this.pushUserIDs = [];
        this.pushGroupIDs = [];
      }
    } else {
      this.participantsNotifyAllUsers = false;
    }
    this.onParticipantsChange();
  }

  public onParticipantsNotifyAllUsersChange(value: boolean): void {
    this.participantsNotifyAllUsers = value && this.calendarEventForm.get('shouldNotify')?.value;
    if (this.participantsNotifyAllUsers) {
      this.pushUserIDs = [];
      this.pushGroupIDs = [];
    }
    this.onParticipantsChange();
  }

  private calculateEventDuration(start: Date, end: Date, isAllDay: boolean): number {
    const daysSpanned = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (isAllDay) {
      return 24 * 60 * daysSpanned;
    } else {
      return Math.round((end.getTime() - start.getTime()) / 60000);
    }
  }

  private adjustUserIDs(userIDs: number[]): number[] {
    if (this.participantsAllUsers) {
      return userIDs;
    }

    const currentUserId = this.profileService.profile()?.user?.id;
    if (currentUserId === undefined) {
      return userIDs;
    }

    const selectedCalendarId = this.canEditCalendarSelection
      ? (this.calendarEventForm.controls['calendar_id'].value ?? this.calendarStore.selectedCalendarID() ?? null)
      : (this.calendarEvent?.calendar_id ?? this.calendarEventForm.controls['calendar_id'].value ?? this.calendarStore.selectedCalendarID() ?? null);
    const selectedCalendar = this.availableCalendars().find((calendar) => calendar.id === selectedCalendarId);
    const shouldIncludeCurrentUser = selectedCalendar?.type === CalendarType.USER
      && (selectedCalendar?.owner?.id ?? -1) === currentUserId;

    if (!shouldIncludeCurrentUser || userIDs.includes(currentUserId)) {
      return userIDs;
    }

    return [...userIDs, currentUserId];
  }

  public onParticipantsChange(): void {
    this.section2Edited = true;
  }

  public onAttachmentFilesChange(files: FileStorageResource[]): void {
    this.attachmentFiles = files;
    this.section0Edited = true;
  }

  private resetSectionEditTracking(): void {
    this.section0Edited = false;
    this.section1Edited = false;
    this.section2Edited = false;
    this.section3Edited = false;
  }

  public getEditedSections(): { section0: boolean; section1: boolean; section2: boolean } {
    return {
      section0: this.section0Edited,
      section1: this.section1Edited,
      section2: this.section2Edited
    };
  }

  public hasAnyEdits(): boolean {
    return this.section0Edited || this.section1Edited || this.section2Edited || this.section3Edited;
  }

  private isInventorySelectionValid(): boolean {
    if (this.inventoryForm.valid) {
      return true;
    }

    Object.values(this.inventoryForm.controls).forEach((control) => {
      control.updateValueAndValidity({emitEvent: false});
      if (control.enabled && control.invalid) {
        control.markAsDirty();
        control.markAsTouched();
      }
    });

    return false;
  }

  public timezoneSearch(searchTerm: string): void {
    this.customTimezone = Intl.supportedValuesOf('timeZone')
      .map((zone: string) => ({name: zone, id: zone}))
      .filter((zone: {name: string; id: string}) => zone.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  private isEventNameValid(): boolean {
    const nameControl = this.calendarEventForm.get('name');
    const nameValue = nameControl?.value;

    if (typeof nameValue !== 'string') {
      return false;
    }

    return nameValue.trim() !== '';
  }

  private markNameControlAsInvalidInteraction(): void {
    const nameControl = this.calendarEventForm.get('name');
    if (!nameControl) {
      return;
    }

    nameControl.markAsDirty();
    nameControl.markAsTouched();
    nameControl.updateValueAndValidity({emitEvent: false});
  }

  public getNameErrorText(): string {
    const control = this.calendarEventForm.get('name');
    if (!control?.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.languageService.getTranslationByKey('INPUTREQUIRED');
    }

    if (control.errors['maxlength']) {
      return this.languageService.getTranslationByKey('INPUTLENGTHTOOLONG');
    }

    return this.languageService.getTranslationByKey('INVALIDINPUT');
  }

  public getDescriptionErrorText(): string {
    const control = this.calendarEventForm.get('description');
    if (!control?.errors) {
      return '';
    }

    if (control.errors['maxlength']) {
      return this.languageService.getTranslationByKey('INPUTLENGTHTOOLONG');
    }

    return this.languageService.getTranslationByKey('INVALIDINPUT');
  }

  public getLocationErrorText(): string {
    const control = this.calendarEventForm.get('location');
    if (!control?.errors) {
      return '';
    }

    if (control.errors['maxlength']) {
      return this.languageService.getTranslationByKey('INPUTLENGTHTOOLONG');
    }

    return this.languageService.getTranslationByKey('INVALIDINPUT');
  }

  private areStepControlsValid(stepIndex: number, markAsTouched: boolean): boolean {
    const stepControlNames: Record<number, string[]> = {
      0: this.getStepZeroControlNames(),
      1: [
        'onDateStart',
        'onDateEnd',
        'isWholeday',
        'startTimeZone',
        'repeatType',
        'repeatEvery',
        'repeatEndType',
        'repeatDaysUntil',
        'repeatDaysCount',
        'repeatOn',
        'monthlyRepeatMode',
        'monthlyRepeatDay',
        'monthlyOrdinal',
        'monthlyWeekday',
        'repeatMonth',
        'shouldNotify',
        'notifyBeforeMinutes',
        'notifyAt'
      ],
      2: [],
      3: this.getStepThreeControlNames(),
      4: ['creatorNotifications']
    };

    const controlsInCurrentStep = stepControlNames[stepIndex] ?? [];
    return this.areNamedControlsValid(controlsInCurrentStep, markAsTouched, stepIndex === 0);
  }

  private areNamedControlsValid(controlNames: string[], markAsTouched: boolean, includeNameCheck: boolean = false): boolean {
    let stepIsValid = true;

    controlNames.forEach(controlName => {
      const control = this.calendarEventForm.get(controlName) ?? this.inventoryForm.get(controlName);
      if (!control || control.disabled) {
        return;
      }

      control.updateValueAndValidity({emitEvent: false});
      if (control.invalid) {
        if (markAsTouched) {
          control.markAsDirty();
          control.markAsTouched();
        }
        stepIsValid = false;
      }
    });

    if (includeNameCheck && !this.isEventNameValid()) {
      if (markAsTouched) {
        this.markNameControlAsInvalidInteraction();
      }
      stepIsValid = false;
    }

    return stepIsValid;
  }

  private isCurrentStepValid(): boolean {
    return this.areStepControlsValid(this.activeStep, true);
  }

  private getStepZeroControlNames(): string[] {
    const controls = ['name', 'priority'];

    if (this.canEditCalendarSelection) {
      controls.unshift('calendar_id');
    }

    if (this.calendarEventForm.get('enableCustomLocation')?.value) {
      controls.push('location');
    }

    return controls;
  }

  private getStepThreeControlNames(): string[] {
    return this.steps[3]?.id === 'resources'
      ? [...Object.keys(this.inventoryForm.controls), 'maxParticipants']
      : ['creatorNotifications'];
  }

  private ensureActiveStepInRange(): void {
    const lastStepIndex = Math.max(0, this.steps.length - 1);
    if (this.activeStep > lastStepIndex) {
      this.activeStep = lastStepIndex;
    }
  }

}
